import React, { useEffect, useRef } from 'react';

// Shared across every instance on the page so the script tag is added once
// even if more than one form ever uses this widget.
let scriptPromise = null;
function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
            script.async = true;
            script.defer = true;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
        });
    }
    return scriptPromise;
}

/*
 * Cloudflare Turnstile — bot check for the contact form, paired with the
 * server-side verification in api/submit-contact/index.js.
 *
 * Renders nothing when `siteKey` is empty, which is the shipped default
 * until a Turnstile widget exists for the domain: the form keeps working,
 * just without this layer, rather than breaking on a missing key.
 *
 * Tokens are single-use. A failed submit should remount this component
 * (give it a new `key` from the parent) rather than try to reset it in
 * place, which is simpler than exposing an imperative reset API for a
 * widget only one form uses today.
 */
export default function TurnstileWidget({ siteKey, onToken }) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useEffect(() => {
        if (!siteKey) return;
        let cancelled = false;

        loadTurnstileScript().then(() => {
            if (cancelled || !containerRef.current || !window.turnstile) return;
            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: siteKey,
                callback: onToken,
                'expired-callback': () => onToken(''),
                'error-callback': () => onToken(''),
            });
        }).catch(() => {
            // Script failed to load (blocked, offline). Verification just
            // never completes, and the server falls back to its own checks.
        });

        return () => {
            cancelled = true;
            if (window.turnstile && widgetIdRef.current != null) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteKey]);

    if (!siteKey) return null;
    return <div ref={containerRef} style={{ margin: '14px 0' }} />;
}
